import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Send, ChevronLeft, ChevronRight,
  Plus, Search, Trash2, Pin, Clock,
  Paperclip, X, RefreshCw, ThumbsUp, ThumbsDown,
  Download, Share2, Lightbulb, Bot, FileSpreadsheet,
  CheckCircle, AlertTriangle, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { sendChatMessage, getSuggestedQuestions, uploadFileForImport, executeImport, uploadForConversation, clearFileContext, getActiveContexts, executeAction } from '../api/aiChat';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../lib/utils';
import RichMessageRenderer from '../components/ai/RichMessageRenderer';


/**
 * Full-Screen AI Chat Page - "Flo" Assistant
 * 
 * Features:
 * - Hybrid sidebar design (collapsible)
 * - Persistent conversation history
 * - Voice input support
 * - File upload support
 * - Regenerate responses
 * - Feedback on responses
 * - Contextual suggestions
 */

// Clean, professional color theme - AutoFlo Blue (#07689f)
const THEME = {
  primary: 'bg-[#07689f]',
  primaryHover: 'hover:bg-[#065a8a]',
  primaryText: 'text-[#07689f]',
  light: 'bg-[#e8f4fa]',
  lightBorder: 'border-[#b8dff5]',
  userBubble: 'bg-gray-900',
};

const AIChatPage = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Conversations State
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Input State
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  // File State
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  // Import State
  const [pendingImport, setPendingImport] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Conversational Import State (Phase 5)
  const [activeFileContext, setActiveFileContext] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load conversations from localStorage on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const saved = localStorage.getItem('flo_conversations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed);
        if (parsed.length > 0) {
          setActiveConversationId(parsed[0].id);
          setMessages(parsed[0].messages || []);
        }
      } catch (e) {
        console.error('Failed to parse conversations:', e);
      }
    }
    
    loadSuggestions();
  }, [isAuthenticated, navigate]);

  // Save conversations to localStorage
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem('flo_conversations', JSON.stringify(conversations));
    }
  }, [conversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSuggestions = async () => {
    try {
      const result = await getSuggestedQuestions();
      if (result.success) {
        setSuggestions(result.questions);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const createNewConversation = () => {
    const newConv = {
      id: Date.now().toString(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pinned: false
    };
    setConversations(prev => [newConv, ...prev]);
    setActiveConversationId(newConv.id);
    setMessages([]);
    inputRef.current?.focus();
  };

  const selectConversation = (conv) => {
    setActiveConversationId(conv.id);
    setMessages(conv.messages || []);
  };

  const deleteConversation = (convId, e) => {
    e.stopPropagation();
    setConversations(prev => prev.filter(c => c.id !== convId));
    if (activeConversationId === convId) {
      const remaining = conversations.filter(c => c.id !== convId);
      if (remaining.length > 0) {
        setActiveConversationId(remaining[0].id);
        setMessages(remaining[0].messages || []);
      } else {
        setActiveConversationId(null);
        setMessages([]);
      }
    }
  };

  const togglePin = (convId, e) => {
    e.stopPropagation();
    setConversations(prev => prev.map(c => 
      c.id === convId ? { ...c, pinned: !c.pinned } : c
    ));
  };

  const updateConversationMessages = useCallback((newMessages, title = null) => {
    setConversations(prev => prev.map(c => {
      if (c.id === activeConversationId) {
        return {
          ...c,
          messages: newMessages,
          title: title || c.title,
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  }, [activeConversationId]);

  const handleSend = async (messageOverride = null) => {
    const messageText = messageOverride || inputValue.trim();
    const hasFiles = attachedFiles.length > 0;
    const importableFile = attachedFiles.find(f => f.isImportable);
    
    // Need either text or files
    if (!messageText && !hasFiles) return;
    if (isLoading) return;

    // Create conversation if none exists
    if (!activeConversationId) {
      createNewConversation();
    }

    // Store files before clearing
    const currentFiles = [...attachedFiles];
    
    setInputValue('');
    setAttachedFiles([]);
    
    // If there's an importable file, process it with the file upload flow
    if (importableFile) {
      await handleFileUploadWithContext(importableFile.file, messageText);
      return;
    }
    
    // Regular message flow
    const userMessage = { 
      id: Date.now().toString(),
      role: 'user', 
      content: messageText || 'Attached file(s)',
      files: currentFiles.length > 0 ? currentFiles : undefined,
      timestamp: new Date().toISOString()
    };
    
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Phase 5: Pass file context ID if active
      const result = await sendChatMessage(messageText, messages, activeFileContext?.id);
      
      const assistantMessage = { 
        id: (Date.now() + 1).toString(),
        role: 'assistant', 
        content: result.response || "I couldn't process that request.",
        response_type: result.response_type || 'text',
        structured_data: result.structured_data,
        action_links: result.action_links,
        follow_up_suggestions: result.follow_up_suggestions,
        toolResults: result.tool_results,
        isError: !result.success,
        timestamp: new Date().toISOString()
      };
      
      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);
      
      // Update conversation title from first message
      const title = messages.length === 0 ? messageText.slice(0, 40) + (messageText.length > 40 ? '...' : '') : null;
      updateConversationMessages(updatedMessages, title);
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        id: (Date.now() + 1).toString(),
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true,
        response_type: 'text',
        timestamp: new Date().toISOString()
      };
      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);
      updateConversationMessages(updatedMessages);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle file upload with user context - Now using Conversational Import (Phase 5)
  const handleFileUploadWithContext = async (file, userContext) => {
    setIsUploading(true);
    
    // Add user message showing file upload with context
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userContext 
        ? `📁 **${file.name}**\n\n${userContext}`
        : `📁 Analyzing file: ${file.name}`,
      files: [{ name: file.name, size: file.size }],
      timestamp: new Date().toISOString()
    };
    
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    
    try {
      // Use conversational import API (Phase 5)
      const result = await uploadForConversation(file);
      
      if (result.success) {
        // Store active file context for conversational flow
        setActiveFileContext({
          id: result.context_id,
          filename: result.filename,
          summary: result.summary,
          ambiguities: result.ambiguities || []
        });
        
        // Build AI response for conversational flow
        let aiContent = `**I've analyzed your file: ${file.name}**\n\n`;
        
        if (userContext) {
          aiContent += `*Your note: "${userContext}"*\n\n`;
        }
        
        aiContent += result.summary || 'File parsed successfully.';
        aiContent += '\n\n';
        
        // Show ambiguities as questions the AI should ask
        const ambiguities = result.ambiguities || [];
        if (ambiguities.length > 0) {
          aiContent += `**I have a few questions before we proceed:**\n\n`;
          ambiguities.forEach((amb, idx) => {
            aiContent += `${idx + 1}. ${amb.question}\n`;
            if (amb.options && amb.options.length > 0) {
              aiContent += `   Options: ${amb.options.join(', ')}\n`;
            }
          });
          aiContent += '\nPlease answer these questions so I can prepare the import correctly.';
        } else {
          aiContent += `**Ready to import when you are!**\n\nJust say "proceed" or "import" to execute, or ask me any questions about the data.`;
        }
        
        const assistantMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: aiContent,
          response_type: 'conversational_import',
          file_context: result,
          timestamp: new Date().toISOString()
        };
        
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages(updatedMessages);
        updateConversationMessages(updatedMessages, `Import: ${file.name}`);
        
        // Also store as pending import for backwards compatibility
        setPendingImport(result);
      } else {
        // Error response
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `❌ **Error analyzing file**\n\n${result.error || 'Unknown error occurred'}\n\nPlease make sure your file has recognizable columns like Part Number, Description, OEM, Volume, or Price.`,
          response_type: 'text',
          isError: true,
          timestamp: new Date().toISOString()
        };
        
        const updatedMessages = [...newMessages, errorMessage];
        setMessages(updatedMessages);
        updateConversationMessages(updatedMessages);
      }
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `❌ **Upload failed**\n\nSorry, I couldn't process that file. Please try again.`,
        response_type: 'text',
        isError: true,
        timestamp: new Date().toISOString()
      };
      
      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);
      updateConversationMessages(updatedMessages);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRegenerate = async (messageIndex) => {
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex < 0 || messages[userMessageIndex]?.role !== 'user') return;
    
    const userMessage = messages[userMessageIndex].content;
    const contextMessages = messages.slice(0, userMessageIndex);
    
    // Remove the old response
    const newMessages = messages.slice(0, messageIndex);
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Phase 5: Pass file context ID if active
      const result = await sendChatMessage(userMessage, contextMessages, activeFileContext?.id);
      
      const assistantMessage = { 
        id: Date.now().toString(),
        role: 'assistant', 
        content: result.response || "I couldn't process that request.",
        response_type: result.response_type || 'text',
        structured_data: result.structured_data,
        action_links: result.action_links,
        follow_up_suggestions: result.follow_up_suggestions,
        isError: !result.success,
        timestamp: new Date().toISOString(),
        regenerated: true
      };
      
      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);
      updateConversationMessages(updatedMessages);
    } catch (error) {
      console.error('Regenerate error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = (messageId, feedback) => {
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, feedback } : m
    ));
    // TODO: Send feedback to backend
    // Feedback logging removed for production
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // File handlers - Smart Import
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Only handle first file for now
    const file = files[0];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    // For Excel/CSV files, attach them (don't auto-upload)
    // User can add context before sending
    if (['xlsx', 'xls', 'csv'].includes(fileExt)) {
      const newFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        isImportable: true // Flag for smart import processing
      };
      setAttachedFiles(prev => [...prev, newFile]);
    } else {
      // For other files, just attach
      const newFiles = files.map(f => ({
        id: Date.now() + Math.random(),
        name: f.name,
        size: f.size,
        type: f.type,
        file: f,
        isImportable: false
      }));
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const formatImportPreview = (result, filename, userContext) => {
    const { detected_type, summary, column_mappings, updates, new_records, warnings, insights } = result;
    
    let preview = `**File Analysis: ${filename}**\n\n`;
    
    // If user provided context, acknowledge it
    if (userContext) {
      preview += `*Your note: "${userContext}"*\n\n`;
    }
    
    // Phase 4: Show insights FIRST - this is the conversational part (professional tone, minimal icons)
    if (insights && insights.length > 0) {
      preview += `**A few things worth noting before we proceed:**\n\n`;
      insights.slice(0, 4).forEach(insight => {
        // Remove emoji icons from insights, use bullet points
        preview += `• ${insight.text}\n`;
        if (insight.detail) {
          preview += `  *${insight.detail}*\n`;
        }
      });
      preview += `\n`;
    }
    
    preview += `**File Summary:**\n`;
    preview += `• Data Type: ${detected_type.charAt(0).toUpperCase() + detected_type.slice(1)}\n`;
    preview += `• Total Rows: ${result.total_rows || 0}\n`;
    preview += `• Confidence: ${Math.round((result.confidence || 0) * 100)}%\n\n`;
    
    // Column mappings (collapsed for cleaner view)
    if (column_mappings && Object.keys(column_mappings).length > 0) {
      preview += `**Detected Fields:** ${Object.keys(column_mappings).map(f => f.replace(/_/g, ' ')).join(', ')}\n\n`;
    }
    
    // Categorize changes by impact
    if (summary) {
      preview += `**Changes Preview:**\n\n`;
      
      if (summary.updates > 0) {
        preview += `**Updates to Existing Records (${summary.updates}):**\n`;
        // Show first 3 updates (more concise)
        const previewUpdates = (updates || []).slice(0, 3);
        previewUpdates.forEach(update => {
          preview += `• **${update.part_number || update.program_name}**\n`;
          (update.changes || []).slice(0, 2).forEach(change => {
            preview += `  - ${change.field}: ${change.current_value} → ${change.new_value}\n`;
          });
        });
        if (summary.updates > 3) {
          preview += `  ...and ${summary.updates - 3} more updates\n`;
        }
        preview += `\n`;
      }
      
      if (summary.new_records > 0) {
        preview += `**New Records to Create (${summary.new_records}):**\n`;
        const previewNew = (new_records || []).slice(0, 3);
        previewNew.forEach(record => {
          preview += `• ${record.part_number || record.program_name}\n`;
        });
        if (summary.new_records > 3) {
          preview += `  ...and ${summary.new_records - 3} more new records\n`;
        }
        preview += `\n`;
      }
    }
    
    // Warnings (keep one warning icon as it's actually important)
    if (warnings && warnings.length > 0) {
      preview += `**Warnings (${warnings.length}):**\n`;
      warnings.slice(0, 2).forEach(w => {
        preview += `• Row ${w.row}: ${w.issue}\n`;
      });
      if (warnings.length > 2) {
        preview += `• ...and ${warnings.length - 2} more warnings\n`;
      }
      preview += `\n`;
    }
    
    preview += `---\n`;
    preview += `**Ready to proceed?** Select a reason below and confirm.`;
    
    return preview;
  };

  // State for reasoning selection
  const [selectedReasoning, setSelectedReasoning] = useState(null);
  const [customReasoningText, setCustomReasoningText] = useState('');

  const handleReasoningSelect = (option) => {
    setSelectedReasoning(option);
    // If not "other", clear custom text
    if (option.id !== 'other') {
      setCustomReasoningText('');
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingImport?.import_id) return;
    
    setIsLoading(true);
    
    try {
      // Phase 4: Include reasoning in the import execution
      const result = await executeImport(
        pendingImport.import_id,
        selectedReasoning?.id || null,
        selectedReasoning?.label || null,
        selectedReasoning?.id === 'other' ? customReasoningText : null
      );
      
      let successMessage = `**Import Complete**\n\n`;
      successMessage += `• ${result.updates_applied || 0} records updated\n`;
      successMessage += `• ${result.new_records_created || 0} new records created\n\n`;
      
      if (result.errors?.length > 0) {
        successMessage += `Note: ${result.errors.length} errors occurred\n\n`;
      } else {
        successMessage += `All records processed successfully.\n\n`;
      }
      
      // Acknowledge reasoning capture
      if (selectedReasoning) {
        successMessage += `*Noted: "${selectedReasoning.label}"*`;
      }
      
      const responseMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: result.success ? successMessage : `**Import Failed**\n\n${result.message}`,
        response_type: 'text',
        isError: !result.success,
        timestamp: new Date().toISOString()
      };
      
      const updatedMessages = [...messages, responseMessage];
      setMessages(updatedMessages);
      updateConversationMessages(updatedMessages);
      
      // Reset state
      setPendingImport(null);
      setSelectedReasoning(null);
      setCustomReasoningText('');
    } catch (error) {
      console.error('Import execution error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelImport = () => {
    setPendingImport(null);
    setSelectedReasoning(null);
    setCustomReasoningText('');
    
    const cancelMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: `Import cancelled. Feel free to upload another file or ask me anything!`,
      response_type: 'text',
      timestamp: new Date().toISOString()
    };
    
    const updatedMessages = [...messages, cancelMessage];
    setMessages(updatedMessages);
    updateConversationMessages(updatedMessages);
  };

  // Clear active file context (Phase 5)
  const handleClearFileContext = async () => {
    if (!activeFileContext?.id) return;
    
    try {
      await clearFileContext(activeFileContext.id);
    } catch (error) {
      console.error('Error clearing context:', error);
    }
    
    setActiveFileContext(null);
    setPendingImport(null);
    setSelectedReasoning(null);
    setCustomReasoningText('');

    const clearMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: `File context cleared. Feel free to upload a new file or ask me anything!`,
      response_type: 'text',
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, clearMessage];
    setMessages(updatedMessages);
    updateConversationMessages(updatedMessages);
  };

  // Handle action confirmation from AI responses (e.g., creating opportunities)
  const handleActionConfirm = async (actionType, actionParams, reason) => {
    setIsLoading(true);
    try {
      // Build reasoning object
      const reasoning = reason ? {
        id: 'custom',
        text: reason,
        custom_text: reason
      } : null;

      // Execute the action
      const result = await executeAction(actionType, actionParams, reasoning);

      if (result.success) {
        // Add success message
        const successMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: result.message || 'Action completed successfully.',
          response_type: 'text',
          action_links: result.action_links || (result.entity_id ? [{
            label: 'View Created Item',
            url: `/pipeline?opportunity=${result.entity_id}`,
            entity_type: 'opportunity'
          }] : []),
          timestamp: new Date().toISOString()
        };
        const updatedMessages = [...messages, successMessage];
        setMessages(updatedMessages);
        updateConversationMessages(updatedMessages);
      } else {
        const errorMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Action failed: ${result.error || 'Unknown error'}`,
          response_type: 'text',
          isError: true,
          timestamp: new Date().toISOString()
        };
        const updatedMessages = [...messages, errorMessage];
        setMessages(updatedMessages);
        updateConversationMessages(updatedMessages);
      }
    } catch (error) {
      console.error('Action execution error:', error);
      const errorMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error executing this action. Please try again.',
        response_type: 'text',
        isError: true,
        timestamp: new Date().toISOString()
      };
      const updatedMessages = [...messages, errorMessage];
      setMessages(updatedMessages);
      updateConversationMessages(updatedMessages);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle action cancellation
  const handleActionCancel = () => {
    const cancelMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: 'Action cancelled. Is there anything else I can help you with?',
      response_type: 'text',
      timestamp: new Date().toISOString()
    };
    const updatedMessages = [...messages, cancelMessage];
    setMessages(updatedMessages);
    updateConversationMessages(updatedMessages);
  };

  // Handle flow completion
  const handleFlowComplete = (data) => {
    // The flow component will send the final create message
    // This callback is for any post-completion cleanup
    // Flow completion logging removed for production
  };

  const removeFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const filteredConversations = conversations
    .filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className={cn(
        "bg-white border-r border-gray-200 flex flex-col transition-all duration-300 flex-shrink-0",
        sidebarOpen ? "w-80" : "w-0 overflow-hidden"
      )}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-100">
          <Button
            onClick={createNewConversation}
            className="w-full justify-start gap-2 bg-[#07689f] hover:bg-[#065a8a] text-white"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
          
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>
        
        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No conversations yet
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={cn(
                  "group flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors",
                  activeConversationId === conv.id
                    ? "bg-[#e8f4fa] border border-[#b8dff5]"
                    : "hover:bg-gray-50"
                )}
              >
                <MessageCircle className={cn(
                  "h-4 w-4 flex-shrink-0",
                  activeConversationId === conv.id ? "text-[#07689f]" : "text-gray-400"
                )} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    {conv.pinned && <Pin className="h-3 w-3 text-[#07689f]" />}
                    <span className="text-sm font-medium truncate">{conv.title}</span>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(conv.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                  <button
                    onClick={(e) => togglePin(conv.id, e)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <Pin className={cn("h-3 w-3", conv.pinned ? "text-[#07689f]" : "text-gray-400")} />
                  </button>
                  <button
                    onClick={(e) => deleteConversation(conv.id, e)}
                    className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Toggle Sidebar Button - attached to left edge of main area */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 border-l-0 rounded-r-lg p-1.5 hover:bg-gray-50 shadow-sm"
        >
          {sidebarOpen ? <ChevronLeft className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
        </button>

        {/* Chat Header - Fixed at top */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#07689f] flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">Flo</h1>
              <p className="text-xs text-gray-500">Your AutoFlo AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-gray-500">
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-500">
              <Share2 className="h-4 w-4 mr-1" />
              Share
            </Button>
          </div>
        </div>

        {/* Messages Area - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-4xl mx-auto px-4 py-6">
            {messages.length === 0 ? (
              // Welcome State
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <div className="w-16 h-16 rounded-full bg-[#07689f] flex items-center justify-center mb-6">
                  <Bot className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Hi, I&apos;m Flo</h2>
                <p className="text-gray-500 mb-8 max-w-md">
                  Your intelligent assistant for AutoFlo. Ask me about your pipeline, opportunities, goals, or anything else.
                </p>
                
                {/* Suggestions Grid */}
                <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
                  {suggestions.slice(0, 4).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(suggestion)}
                      className="flex items-start gap-3 p-4 text-left bg-white border border-gray-200 rounded-lg hover:border-[#5eb3db] hover:shadow-sm transition-all"
                    >
                      <Lightbulb className="h-4 w-4 text-[#07689f] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{suggestion}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              // Messages
              <div className="space-y-6">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={cn(
                      "flex gap-3",
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-[#07689f] flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    <div className={cn(
                      "max-w-[80%] rounded-2xl",
                      msg.role === 'user'
                        ? "bg-gray-900 text-white px-4 py-3"
                        : msg.isError
                        ? "bg-red-50 text-red-700 border border-red-200 px-4 py-3"
                        : "bg-white border border-gray-200 px-4 py-3 shadow-sm"
                    )}>
                      {msg.role === 'user' ? (
                        <div>
                          <div className="text-sm">{msg.content}</div>
                          {msg.files && msg.files.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {msg.files.map(f => (
                                <span key={f.id} className="text-xs bg-white/20 px-2 py-0.5 rounded">
                                  📎 {f.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <RichMessageRenderer
                            message={{
                              content: msg.content,
                              response_type: msg.response_type,
                              structured_data: msg.structured_data,
                              action_links: msg.action_links,
                              follow_up_suggestions: msg.follow_up_suggestions,
                              tool_results: msg.toolResults
                            }}
                            onFollowUpSelect={(followUp) => handleSend(followUp)}
                            onNavigate={(url) => navigate(url)}
                            onSendMessage={handleSend}
                            onActionConfirm={handleActionConfirm}
                            onActionCancel={handleActionCancel}
                            onFlowComplete={handleFlowComplete}
                            isLoading={isLoading}
                          />
                          
                          {/* Import Action Buttons with Reasoning Selection */}
                          {msg.response_type === 'import_preview' && pendingImport && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                              {/* Reasoning Options - Phase 4 Conversational UX */}
                              {pendingImport.reasoning_options && pendingImport.reasoning_options.length > 0 && (
                                <div className="mb-4">
                                  <p className="text-sm text-gray-600 mb-2">
                                    Quick note for future reference — why this import?
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {pendingImport.reasoning_options.map((option) => (
                                      <button
                                        key={option.id}
                                        onClick={() => handleReasoningSelect(option)}
                                        className={cn(
                                          "px-3 py-1.5 text-sm rounded-full border transition-all",
                                          selectedReasoning?.id === option.id
                                            ? "bg-[#07689f] text-white border-[#07689f]"
                                            : "bg-white text-gray-700 border-gray-200 hover:border-[#07689f] hover:text-[#07689f]"
                                        )}
                                      >
                                        {option.label}
                                      </button>
                                    ))}
                                  </div>
                                  
                                  {/* Custom reasoning input */}
                                  {selectedReasoning?.id === 'other' && (
                                    <div className="mt-2">
                                      <input
                                        type="text"
                                        value={customReasoningText}
                                        onChange={(e) => setCustomReasoningText(e.target.value)}
                                        placeholder="Brief reason for this import..."
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#07689f]"
                                        autoFocus
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Action buttons */}
                              <div className="flex items-center gap-3">
                                <Button
                                  onClick={handleConfirmImport}
                                  disabled={isLoading}
                                  className="bg-[#07689f] hover:bg-[#065a8a] text-white"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  {selectedReasoning ? 'Confirm Import' : 'Import (Skip Reason)'}
                                </Button>
                                <Button
                                  onClick={handleCancelImport}
                                  variant="outline"
                                  disabled={isLoading}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Cancel
                                </Button>
                              </div>
                              
                              {/* Encouragement to add reasoning */}
                              {!selectedReasoning && (
                                <p className="text-xs text-gray-400 mt-2">
                                  💡 Adding context helps Flo provide better insights in future imports
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Message Actions */}
                          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleRegenerate(idx)}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#07689f]"
                              disabled={isLoading}
                            >
                              <RefreshCw className="h-3 w-3" />
                              Regenerate
                            </button>
                            <div className="flex items-center gap-1 ml-auto">
                              <button
                                onClick={() => handleFeedback(msg.id, 'positive')}
                                className={cn(
                                  "p-1 rounded hover:bg-gray-100",
                                  msg.feedback === 'positive' ? "text-[#07689f]" : "text-gray-400"
                                )}
                              >
                                <ThumbsUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleFeedback(msg.id, 'negative')}
                                className={cn(
                                  "p-1 rounded hover:bg-gray-100",
                                  msg.feedback === 'negative' ? "text-red-600" : "text-gray-400"
                                )}
                              >
                                <ThumbsDown className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-white text-xs font-medium">
                        {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#07689f] flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Uploading indicator */}
                {isUploading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#07689f] flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-[#07689f] animate-pulse" />
                        <span className="text-sm text-gray-600">Analyzing your file...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area - Fixed at bottom */}
        <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            {/* Active File Context Badge - Near file upload (Phase 5) */}
            {activeFileContext && (
              <div className="flex items-center justify-between bg-[#e8f4fa] border border-[#b8dff5] rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-[#07689f]" />
                  <span className="text-sm font-medium text-gray-700">{activeFileContext.filename}</span>
                  <span className="text-xs text-[#07689f] bg-white px-2 py-0.5 rounded-full">Active in conversation</span>
                </div>
                <button
                  onClick={handleClearFileContext}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 px-2 py-1 hover:bg-red-50 rounded"
                  title="Clear file context"
                >
                  <X className="h-3 w-3" />
                  <span>Clear Context</span>
                </button>
              </div>
            )}
            
            {/* Attached Files */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {attachedFiles.map(file => (
                  <div key={file.id} className="flex items-center gap-2 bg-[#e8f4fa] border border-[#b8dff5] px-3 py-1.5 rounded-full text-sm">
                    <FileSpreadsheet className="h-3 w-3 text-[#07689f]" />
                    <span className="text-gray-700 truncate max-w-[200px]">{file.name}</span>
                    <button onClick={() => removeFile(file.id)} className="text-gray-400 hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-end gap-2">
              {/* File Upload */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".xlsx,.xls,.csv"
                className="hidden"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || isUploading}
                className="text-gray-500 hover:text-[#07689f] h-10 w-10 p-0"
                title="Attach Excel or CSV file"
              >
                <Paperclip className="h-5 w-5" />
              </Button>
              
              {/* Text Input */}
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={activeFileContext 
                    ? `Ask about ${activeFileContext.filename} or say "proceed" to import...`
                    : attachedFiles.length > 0 
                    ? "Add context about this file (optional), then click send..." 
                    : "Ask Flo anything or attach an Excel file to import data..."}
                  rows={1}
                  className="w-full px-4 py-3 pr-12 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#07689f] focus:border-transparent resize-none"
                  disabled={isLoading || isUploading}
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                />
              </div>
              
              {/* Send Button */}
              <Button
                onClick={() => handleSend()}
                disabled={(!inputValue.trim() && attachedFiles.length === 0) || isLoading}
                className="h-12 w-12 rounded-xl bg-[#07689f] hover:bg-[#065a8a]"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-2">
              Flo can help with pipeline, opportunities, goals, claims, and more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
